import React, { useEffect, useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "../services/api";

// Detecta dispositivo táctil (móvil/tablet), sin depender de qué navegador sea
const esDispositivoMovil = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);

export default function Ticket({ invoice, onAfterPrint }) {
  const [company, setCompany] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrlMovil, setPdfUrlMovil] = useState(null);
  const ticketRef = useRef(null);
  const esMovil = useRef(esDispositivoMovil()).current;

  // ── useEffect 1: cargar datos de empresa (SOLO se usa en PC, para la vista previa) ──
  useEffect(() => {
    if (esMovil) {
      // En móvil no se necesita: el PDF ya trae los datos de la empresa.
      setIsReady(true);
      return;
    }
    api.get('/company/info')
      .then(r => {
        setCompany(r.data);
        setIsReady(true);
      })
      .catch(err => {
        setError(err.message);
        setCompany({ nombre: "EMPRESA DEMO", direccion: "Dirección no disponible", telefono: "0000-0000", email: "info@empresa.com", nit: "0000-000000-000-0", nrc: "000000-0" });
        setIsReady(true);
      });
  }, [esMovil]);

  // ── useEffect 2: imprimir cuando llega la factura ──
  useEffect(() => {
    if (!invoice || !isReady) return;

    let cancelado = false;

    const timer = setTimeout(async () => {
      try {
        const saleId = invoice.id;
        const token = localStorage.getItem('token');

        if (esMovil) {
          // ── RAMA MÓVIL: URL directa del backend (no blob).
          // Necesaria para que Android/iOS puedan abrir e imprimir el PDF
          // correctamente. El token va como parámetro porque una URL
          // abierta directo en el navegador no puede llevar headers.
          const urlMovil = `https://altivaweb.com/FACTURACION/BACK/public/api/invoices/ticket-movil/${saleId}?token=${encodeURIComponent(token)}`;
          if (!cancelado) setPdfUrlMovil(urlMovil);
          return;
        }

        // ── RAMA PC: comportamiento original, intacto ──
        const puntoVentaId = localStorage.getItem('punto_venta_id');
        const url = `https://altivaweb.com/FACTURACION/BACK/public/api/invoices/ticket/${saleId}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Punto-Venta': puntoVentaId,
          }
        });

        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);

        if (cancelado) return;

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = pdfUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(pdfUrl);
          }, 2000);
        };
        if (onAfterPrint) onAfterPrint();

      } catch (err) {
        console.error('Error generando ticket PDF:', err);
        if (esMovil && onAfterPrint) onAfterPrint();
      }
    }, 1500);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [invoice, isReady, esMovil, onAfterPrint]);

  // ── Renderizar el PDF como imágenes (canvas) usando PDF.js ──
  // Esto evita depender de si el navegador soporta <embed>/<object> o de
  // cómo maneje cada uno su visor nativo — funciona igual en Android,
  // iPhone y PC, porque el resultado es HTML/canvas puro, no un PDF
  // que el navegador decide cómo mostrar.
  const canvasContainerRef = useRef(null);
  const [pdfRenderizado, setPdfRenderizado] = useState(false);
  const [errorRenderizado, setErrorRenderizado] = useState(false);

  useEffect(() => {
    if (!esMovil || !pdfUrlMovil) return;

    let cancelado = false;

    const cargarPdfJs = () =>
      new Promise((resolve, reject) => {
        if (window.pdfjsLib) return resolve(window.pdfjsLib);
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          resolve(window.pdfjsLib);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });

    const renderizar = async () => {
      try {
        const pdfjsLib = await cargarPdfJs();
        const pdf = await pdfjsLib.getDocument(pdfUrlMovil).promise;

        if (cancelado || !canvasContainerRef.current) return;
        canvasContainerRef.current.innerHTML = '';

        for (let numPagina = 1; numPagina <= pdf.numPages; numPagina++) {
          const pagina = await pdf.getPage(numPagina);
          const escala = 2; // buena nitidez para impresión térmica
          const viewport = pagina.getViewport({ scale: escala });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.maxWidth = '380px';
          canvas.style.display = 'block';
          canvas.style.marginBottom = '8px';

          const ctx = canvas.getContext('2d');
          await pagina.render({ canvasContext: ctx, viewport }).promise;

          if (cancelado) return;
          canvasContainerRef.current.appendChild(canvas);
        }

        if (!cancelado) setPdfRenderizado(true);
      } catch (err) {
        console.error('Error renderizando PDF con PDF.js:', err);
        if (!cancelado) setErrorRenderizado(true);
      }
    };

    renderizar();

    return () => { cancelado = true; };
  }, [esMovil, pdfUrlMovil]);

  const handleImprimirMovil = () => {
    window.print();
    setTimeout(() => {
      if (onAfterPrint) onAfterPrint();
    }, 3000);
  };

  if (!invoice || !isReady) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px' }}>
      Cargando...
    </div>
  );

  // ══════════════════════════════════════════════
  // RAMA MÓVIL: PDF renderizado como imagen + botón imprimir
  // ══════════════════════════════════════════════
  if (esMovil) {
    return (
      <div
        className="ticket-loading-wrapper"
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          minHeight: '100vh',
          fontSize: '18px',
          flexDirection: 'column',
          gap: '12px',
          padding: '10px',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <style>
          {`
            @media print {
              * { visibility: hidden !important; }
              .ticket-canvas-container, .ticket-canvas-container * {
                visibility: visible !important;
              }
              .ticket-canvas-container {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
              }
              .ticket-canvas-container canvas {
                max-width: 100% !important;
              }
            }
          `}
        </style>

        {!pdfUrlMovil && <div>Generando ticket...</div>}
        {pdfUrlMovil && !pdfRenderizado && !errorRenderizado && <div>Cargando ticket...</div>}
        {errorRenderizado && <div>No se pudo cargar el ticket. Intenta de nuevo.</div>}

        <div ref={canvasContainerRef} className="ticket-canvas-container" style={{ width: '100%', maxWidth: '380px' }} />

        {pdfRenderizado && (
          <button
            onClick={handleImprimirMovil}
            style={{
              background: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '14px 28px',
              fontSize: '15px',
              fontWeight: 'bold',
              borderRadius: '8px',
              cursor: 'pointer',
              width: '100%',
              maxWidth: '380px',
              marginBottom: '16px',
              flexShrink: 0,
            }}
          >
            🖨️ Imprimir Ticket
          </button>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════
  // RAMA PC: vista previa completa, tal como estaba (SIN TOCAR)
  // ══════════════════════════════════════════════
  const sale = invoice;
  const fel = invoice.invoice;
  const client = invoice.client ?? {};
  const details = invoice.sale_details ?? invoice.saleDetails ?? [];
  const efectivo = invoice.cash ?? sale.cash ?? sale.efectivo ?? 0;
  const cambio = invoice.change ?? sale.change ?? sale.cambio ?? 0;
  const numeroControl = fel?.numero_control;
  const tipoDocumento = fel?.tipo_documento || sale?.tipo_documento || "01";
  const esCreditoFiscal = tipoDocumento === "03";
  const esConsumidorFinal = tipoDocumento === "01";

  const obtenerTituloDocumento = () => {
    if (esCreditoFiscal) return "CRÉDITO FISCAL";
    if (esConsumidorFinal) return "CONSUMIDOR FINAL";
    return "FACTURA";
  };

  const obtenerNombreTipoDocumento = (codigo) => {
    const tipos = { '36': 'NIT', '13': 'DUI', '37': 'Otro', '03': 'Pasaporte', '02': 'Carnet de Residente' };
    return tipos[codigo] || 'Documento';
  };

  return (
    <>
      <style>
        {`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #f7fafc; }
        .ticket-wrapper { display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; }
        .ticket { width: 80mm; font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; background: white; padding: 8mm; box-shadow: 0 4px 12px rgba(0,0,0,0.15); margin: 0 auto; }
        .ticket-center { text-align: center; }
        .ticket-company { font-weight: bold; font-size: 14px; margin-bottom: 6px; }
        .ticket-info { font-size: 10px; line-height: 1.4; }
        .ticket-info-line { margin: 2px 0; }
        .ticket-divider { border: none; border-top: 1px dashed #000; margin: 10px 0; }
        .ticket-section-title { font-weight: bold; font-size: 13px; text-align: center; margin: 10px 0 6px 0; }
        .ticket-row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; }
        .ticket-row-bold { font-weight: bold; }
        .ticket-table-header { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 4px; margin-bottom: 6px; font-size: 10px; }
        .ticket-table-row { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; margin: 4px 0; font-size: 10px; }
        .ticket-table-cell { text-align: right; }
        .ticket-table-cell:first-child { text-align: left; }
        .ticket-total { font-size: 15px; font-weight: bold; margin: 10px 0; }
        .ticket-qr { text-align: center; margin: 12px 0; }
        .ticket-footer { text-align: center; margin-top: 12px; }
        .text-small { font-size: 10px; }
        .text-bold { font-weight: bold; }
        .error-badge { background-color: #fee; border: 1px solid #fcc; padding: 8px; margin-bottom: 10px; border-radius: 4px; font-size: 10px; color: #c00; }
        @media print {
          * { visibility: hidden; }
          .ticket-wrapper, .ticket-wrapper * { visibility: visible; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; overflow: visible !important; }
          .ticket-wrapper { position: absolute; left: 0; top: 0; width: 80mm; padding: 0; margin: 0; }
          .ticket { box-shadow: none; margin: 0; padding: 5mm; width: 80mm; font-size: 14px; }
          .ticket-company { font-size: 17px; }
          .ticket-info { font-size: 12px; }
          .ticket-section-title { font-size: 15px; }
          .ticket-row { font-size: 13px; }
          .ticket-table-header { font-size: 12px; }
          .ticket-table-row { font-size: 12px; }
          .ticket-total { font-size: 17px; }
          .text-small { font-size: 12px; }
          .error-badge { display: none; }
        }
        `}
      </style>

      <div className="ticket-wrapper">
        <div className="ticket" ref={ticketRef}>

          {error && (
            <div className="error-badge">
              ⚠️ No se pudieron cargar los datos de la empresa. Usando datos por defecto.
            </div>
          )}

          <div className="ticket-center">
            <div className="ticket-section-title" style={{ marginTop: 0 }}>EMISOR</div>
            <div className="ticket-company">
              {company?.nombre_comercial || company?.nombre || 'NOMBRE DE EMPRESA'}
            </div>
            <div className="ticket-info" style={{ marginBottom: '4px' }}>
              <div className="ticket-info-line">{company?.nombre || ''}</div>
            </div>
            <div className="ticket-info">
              <div className="ticket-info-line"><strong>NIT:</strong> {company?.nit || 'N/A'}</div>
              <div className="ticket-info-line"><strong>NRC:</strong> {company?.nrc || 'N/A'}</div>
              {company?.desc_actividad && (
                <div className="ticket-info-line">
                  <strong>Actividad Económica:</strong>
                  <div>{company.desc_actividad}</div>
                </div>
              )}
              <div className="ticket-info-line"><strong>Dirección:</strong> {company?.direccion || 'N/A'}</div>
              <div className="ticket-info-line"><strong>Tel:</strong> {company?.telefono || 'N/A'}</div>
              <div className="ticket-info-line"><strong>Email:</strong> {company?.email || 'N/A'}</div>
            </div>
          </div>

          <hr className="ticket-divider" />

          <div className="ticket-section-title">DOCUMENTO TRIBUTARIO ELECTRÓNICO</div>
          <div className="ticket-center">
            <div className="text-bold" style={{ fontSize: '14px' }}>{obtenerTituloDocumento()}</div>
          </div>

          <hr className="ticket-divider" />

          <div className="text-small">
            <div><strong>Número Control:</strong></div>
            <div style={{ wordBreak: 'break-all', marginBottom: '4px' }}>{numeroControl || 'N/A'}</div>
            <div><strong>Código Generación:</strong></div>
            <div style={{ wordBreak: 'break-all', marginBottom: '4px' }}>{fel?.codigo_generacion || 'N/A'}</div>
            {fel?.sello_recepcion && (
              <>
                <div><strong>Sello de Recepción:</strong></div>
                <div style={{ wordBreak: 'break-all', marginBottom: '4px' }}>{fel.sello_recepcion}</div>
              </>
            )}
            <div><strong>Fecha:</strong> {fel?.fecha_emision || new Date().toLocaleDateString()}</div>
          </div>

          <hr className="ticket-divider" />

          <div className="ticket-row">
            <span><strong>Cajero:</strong></span>
            <span>{sale?.cajero ?? "Sistema"}</span>
          </div>
          <div className="ticket-row">
            <span><strong>Caja:</strong></span>
            <span>{sale?.caja ?? "Caja 1"}</span>
          </div>

          <hr className="ticket-divider" />

          <div className="text-bold" style={{ marginBottom: '4px' }}>CLIENTE</div>

          {esConsumidorFinal ? (
            <div>{client?.nombre || 'Cliente General'}</div>
          ) : (
            <div>
              <div><strong>Nombre:</strong> {client?.nombre || 'Cliente General'}</div>
              {(client?.numero_documento || client?.nit || client?.num_documento) && (
                <div><strong>{obtenerNombreTipoDocumento(client.tipo_documento)}:</strong> {client.numero_documento || client.nit || client.num_documento}</div>
              )}
              {client?.nrc && <div><strong>NRC:</strong> {client.nrc}</div>}
              {(client?.desc_actividad || client?.giro) && <div><strong>Giro:</strong> {client.desc_actividad || client.giro}</div>}
              {client?.direccion && <div style={{ fontSize: '10px', marginTop: '4px' }}><strong>Dirección:</strong> {client.direccion}</div>}
              {client?.telefono && <div><strong>Tel:</strong> {client.telefono}</div>}
              {(client?.correo || client?.email) && <div><strong>Email:</strong> {client.correo || client.email}</div>}
            </div>
          )}

          <hr className="ticket-divider" />

          <div className="ticket-table-header">
            <div>Descripción</div>
            <div className="ticket-table-cell">Cant.</div>
            <div className="ticket-table-cell">P.U.</div>
            <div className="ticket-table-cell">Total</div>
          </div>

          {details.length === 0 && (
            <div className="ticket-center text-small">No hay productos</div>
          )}

          {details.map((item, i) => (
            <div key={i} className="ticket-table-row">
              <div>{item.nombre_producto_snapshot ?? item.product?.nombre ?? "Producto"}</div>
              <div className="ticket-table-cell">{item.cantidad}</div>
              <div className="ticket-table-cell">${Number(item.precio_unitario_snapshot ?? 0).toFixed(2)}</div>
              <div className="ticket-table-cell">${(Number(item.cantidad) * Number(item.precio_unitario_snapshot ?? 0)).toFixed(2)}</div>
            </div>
          ))}

          <hr className="ticket-divider" />

          <div className="ticket-row ticket-total">
            <span>TOTAL:</span>
            <span>${Number(sale?.monto_final ?? 0).toFixed(2)}</span>
          </div>

          {sale?.monto_letras && (
            <div className="ticket-center text-small" style={{ marginTop: '6px' }}>{sale.monto_letras}</div>
          )}

          <hr className="ticket-divider" />

          <div className="ticket-row">
            <span>Efectivo Recibido:</span>
            <span>${Number(efectivo).toFixed(2)}</span>
          </div>
          <div className="ticket-row ticket-row-bold">
            <span>Cambio:</span>
            <span>${Number(cambio).toFixed(2)}</span>
          </div>

          <hr className="ticket-divider" />

          {fel?.codigo_generacion && (
            <div className="ticket-qr">
              <QRCodeCanvas
                value={`${"https://altivaweb.com/FACTURACION/BACK/public"}/factura/${fel.codigo_generacion}`}
                size={140}
                level="M"
                includeMargin={true}
              />
              <div className="text-small" style={{ marginTop: '8px' }}>
                Escanee para descargar PDF y JSON
              </div>
            </div>
          )}

          <hr className="ticket-divider" />

          <div className="ticket-footer text-bold" style={{ fontSize: '13px' }}>¡GRACIAS POR SU COMPRA!</div>
          <div className="ticket-footer text-small" style={{ marginTop: '6px' }}>Conserve este documento para cualquier reclamo</div>

        </div>
      </div>
    </>
  );
}